import { useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import useState from "react-usestateref";
import { createContainer } from "unstated-next";
import useSaasAndThinkAppState from "./state/useSaasAndThinkAppState";
import { useHomeReactFlowState } from "./state/useHomeReactFlowState";
import { useHomeToolBarState } from "./state/useHomeToolBarState";
import { useSignal } from "@preact/signals-react";
import { useAiSaasState } from "./state/useAiSaasState";
import { useWindowSize } from "./state/useWindowSize";
import useChoosingFormatState from "./state/useChoosingFormatState";
import { useNewOnboardingFlowState } from "./state/useNewOnboardingFlowState";
import { useParams } from "react-router-dom";
import func from "@uikit/func";

type HomeRootContainerProps = {
  share?: boolean;
  clickLogOut: () => void;
};

export type HomeRootContainerState = ReturnType<typeof useContainer>;

/**
 * HomeUsePlugin 容器
 *
 * - 该容器应只提供 `HomeUsePlugin` 根组件状态，其他相关逻辑/方法因抽离该容器
 * - 只要是 HomeUsePlugin 组件子节点都可以通过 `useHomeUsePluginContainer` 得到内部数据和逻辑，可避免深度传递
 *
 * 已挂载的状态：
 * - guideTrigger
 * - useSaasAndThinkAppState: saas 和 saasV2, thinkApp 预览数据的状态
 * - useHomeReactFlowState: ReactFlow 组件相关节点，边，渲染时机状态等核心状态
 * - toolBar 的状态，figmaMode, 是否允许画布移动等状态
 * - webConfig 状态， web.config.json 配置状态
 * - cot feedback 状态
 * - reactFlowState: ReactFlow 组件某些配置的状态
 *
 * @returns
 */
function useContainer(props?: HomeRootContainerProps) {
  const urlParam = useParams();

  // redux state
  const imagicaStudioTutorial = useSelector((state: any) => state.fot.imagicaStudioTutorial);

  const [guideTriggerMessage, setGuideTriggerMessage] = useState<{
    cot: number | null | (() => void);
    manual: number | null | (() => void);
  }>({
    cot: null,
    manual: null,
  });

  // saas/saasV2/think/ state
  const saasAndThinkState = useSaasAndThinkAppState();

  // ReactFlow 组件画布相关状态
  const homeReactFlowState = useHomeReactFlowState();

  // 画布 toolBar 相关状态
  const homeToolBarState = useHomeToolBarState();

  const newOnboardingFlowState = useNewOnboardingFlowState();
  // window size and zoom
  const windowSizeState = useWindowSize();

  // start by choosing a publishing format state
  const choosingFormatState = useChoosingFormatState();

  // 其他状态
  const showNavPageState = useState(func.isEmpty(urlParam.attrIndexer));

  // FIXME: 暂时将 aisaas 和 homeuseplugin 公用的数据状态挂在到 home 容器上
  const aiSaasState = useAiSaasState();
  const runAllLoadingState = useState(false);

  // for node title
  const inputTitleState = useSignal<{ id: string; title: string }>({ id: "", title: "" });

  const [enableCOTFeedbackPopup, setEnableCOTFeedbackPopup] = useState(false);

  const enableCOTFeedbackPopupState = { enableCOTFeedbackPopup, setEnableCOTFeedbackPopup };

  const guideTriggerMessageState = {
    guideTriggerMessage,
    triggerGuideManual: (callback?: () => void) =>
      setGuideTriggerMessage(msg => ({ ...msg, manual: callback || Date.now() })),
    triggerGuideCot: (callback?: () => void) =>
      setGuideTriggerMessage(msg => ({ ...msg, cot: callback || Date.now() })),
    imagicaStudioTutorial,
  };

  const { allowZoomOrPan, figmaMode } = homeToolBarState;
  const reactFlowState = {
    allowZoomOrPan,
    panOnDrag: allowZoomOrPan ? !figmaMode : allowZoomOrPan,
    panOnScroll: allowZoomOrPan ? figmaMode : allowZoomOrPan,
    selectionOnDrag: allowZoomOrPan ? figmaMode : allowZoomOrPan,
    nodesDraggable: allowZoomOrPan,
    zoomOnScroll: allowZoomOrPan,
    zoomOnPinch: allowZoomOrPan,
    elementsSelectable: allowZoomOrPan ? !imagicaStudioTutorial.showGuide : allowZoomOrPan,
  };
  const isShare = !!props?.share;

  // FIXME: 该状态为了给 AISaas 组件提供的状态，但是 AISaas 状态中用到的组件可能不在 AISaas 容器下，将该状态挂载到 home 上
  // 最好的解决方法应该将调用 aisaas 容器的组件移动到 aisaas 容器下
  const aiSaasPreviewHoverData = useSignal<AISaas.previewHoverDataType>({
    show: false,
    container: null,
    type: "",
    id: "",
  });

  /// group 手动删除某个node
  const [groupDeleteChildNode, setGroupDeleteChildNode] = useState({ id: "", parentNodeId: "" });
  const preferenceJson = useSignal([]);
  const clickLogOut = props?.clickLogOut;
  return {
    guideTriggerMessageState,
    preferenceJson,

    reactFlowState,

    ...homeToolBarState,

    newOnboardingFlowState,

    enableCOTFeedbackPopupState,
    inputTitleState,
    isShare,

    showNavPageState,

    /**
     * saas,saasv2,thinkapp 状态
     */
    saasAndThinkState,
    displayThought: saasAndThinkState.displayThought,
    setDisplayThought: saasAndThinkState.setDisplayThought,

    // window state
    windowSizeState,
    windowZoom: windowSizeState.zoom,

    /**
     * 画布相关状态
     */
    homeReactFlowState,
    nodesState: homeReactFlowState.nodesState,
    edgesState: homeReactFlowState.edgesState,
    isNewEdgeRunRef: homeReactFlowState.isNewEdgeRunRef,
    nodeIndexRef: homeReactFlowState.nodeIndexRef,
    edgeIndexRef: homeReactFlowState.edgeIndexRef,

    aiSaasState,
    runAllLoadingState,
    aiSaasPreviewHoverData,
    groupDeleteChildNode,
    setGroupDeleteChildNode,
    choosingFormatState,
    clickLogOut,
  };
}

export const HomeUsePluginContainer = createContainer(useContainer);

export const { Provider: HomeUsePluginProvider, useContainer: useHomeUsePluginContainer } = HomeUsePluginContainer;
